import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Modal, Box } from '@material-ui/core';
import styles from './InterPlantAssignDateModal.module.css';
import { API_RESOURCE_URLS, DATE_FORMAT } from '../../constants';
import { CustomDatePicker } from '../FormComponents';
import moment from 'moment';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { API } from '../../apis/api';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import { buildErrorMessage } from '../../apis/calls';

const InterPlantAssignDateModal = ({
  title,
  open,
  handleClose,
  rowsData,
  handleSubmit,
  lineBackgroundColor,
  cancelButtonText = 'CANCEL',
  submitButtonText = 'ASSIGN',
}) => {
  const [plant] = usePlantAndLinesStore().plantState;
  const { showError } = usePopupManager();
  const [date, setDate] = useState(
    dayjs().format(DATE_FORMAT.ISO)
  );
  const [error, setError] = useState(null);
  const [lastAgsnDate, setAggregateLastAgsnDate] = useState();
  const [earliestAppendableDate, setEarliestAppendableDate] = useState(
    moment(0).format(DATE_FORMAT.ISO),
  );
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(
    false,
  );

  const fetchLastAgSNGeneratedDate = async (plantId, lineId) => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.lastAgSNGeneratedDate(plantId, lineId),
      );
      const { data } = response;
      const lastAgsnDate = data.date ? moment(data.date) : null;
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Last AGSN Date`,
        error: error,
      });
    }
  };

  const fetchEarliestAppendableDate = async (plantId, lineId) => {
    try {
      const response = await API.get(
        API_RESOURCE_URLS.emergencyAgsnEarliestAsnAppendableAdate(
          plantId,
          lineId,
        ),
      );
      const { data } = response;
    } catch (error) {
      console.error(buildErrorMessage(error));
      showError({
        contextText: `Failed to Fetch Earliest Asn appendable Date`,
        error: error,
      });
    }
  };

  useEffect(() => {
    if (plant.value && rowsData) {
      fetchLastAgSNGeneratedDate(plant.value, rowsData[0].lineId);
      fetchEarliestAppendableDate(plant.value, rowsData[0].lineId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant, rowsData]);

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <div className={styles.formContainer}>
          <div className={styles.formTitle}>
            <div>
              <span>{title}</span>
            </div>
            <div
              className={styles.formlLineDiv}
              style={{
                backgroundColor: lineBackgroundColor,
              }}
            />
          </div>
          <div className={styles.subHeading}>
            Note: This will generate InterPlant Aggregate Assignment for that particular date
          </div>
          <Box bgcolor="#dadada3d" paddingTop="1px" paddingBottom="1px">
            <div className={styles.defineRow}>
              <label className={styles.label}>Select Date *</label>
              <CustomDatePicker
                isMandatory
                markIfUnselected={highlightMandatoryFields}
                autoOk
                inputVariant="outlined"
                value={date}
                onChange={date => {
                  setDate(date.format(DATE_FORMAT.ISO));
                }}
                inputProps={{
                  className: clsx(styles.dateInput),
                }}
                emptyLabel="dd/mm/yyyy"
                minDate={moment().format(DATE_FORMAT.ISO)}
                format="DD/MM/yyyy"
              />
            </div>
            <div className={styles.errorText}>
              <em>{error}</em>
            </div>
          </Box>
          <div className={styles.bottomRow}>
            <button className={styles.cancelBt} onClick={handleClose}>
              {cancelButtonText}
            </button>
            <span>
              <button
                onClick={() => {
                  if (date) {
                    handleSubmit(rowsData, date);
                    handleClose();
                  } else {
                    setHighlightMandatoryFields(true);
                    setError('*Please select date');
                  }
                }}
                className={styles.submitBt}
                data-testid="assign-date-button"
              >
                {submitButtonText}
              </button>
            </span>
          </div>
        </div>
      </Modal>
    </>
  );
};

InterPlantAssignDateModal.propTypes = {
  title: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      data: PropTypes.array.isRequired,
      filteredData: PropTypes.array.isRequired,
    }),
  ),
  handleSubmit: PropTypes.func.isRequired,
  cancelButtonText: PropTypes.string,
  submitButtonText: PropTypes.string,
  lineBackgroundColor: PropTypes.string,
};

export default InterPlantAssignDateModal;
